import { PhotoCamera } from "@mui/icons-material";
import { Avatar, Badge, Box, Button, CircularProgress, IconButton, Stack, Tooltip } from "@mui/material";
import { COLORS } from "assets";
import { MyModal } from "components";
import { ENDPOINTS, personPlaceholderImage } from "config/constants";
import { useContextController } from "context";
import { login } from "context/actions";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { Fragment, createRef, useCallback, useEffect, useMemo, useState } from "react";
import { UserModel } from "types";

function getInitials(name?: string) {
  if (!name?.trim()) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
}

interface Data {
  user?: UserModel;
}

interface IProfilePicture {
  onSuccess?: () => void;
  size?: number;
}

export default function ProfilePicture({ onSuccess, size = 120 }: IProfilePicture) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File>();

  const t = useTranslation();
  const objectURL = useMemo(() => (file ? URL.createObjectURL(file) : undefined), [file]);
  const { isLoading, response, error, callFetch: callFetchPut } = useFetch<Data>("PUT");
  const [{ userData }, dispatch] = useContextController();
  const { enqueueSnackbar } = useSnackbar();
  const filRef = createRef<HTMLInputElement>();

  const onSubmit = useCallback(
    (remove?: boolean) => {
      const formData = new FormData();
      if (remove) {
        formData.append("removeImage", "true");
      } else if (file) {
        formData.append("image", file);
      } else {
        return;
      }
      callFetchPut(ENDPOINTS.account, { body: formData, headers: {} }).catch(console.error);
    },
    [file, callFetchPut]
  );

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      if (response.data?.user) {
        login(response.data.user, dispatch);
      }
      onSuccess?.();
      setOpen(false);
      setFile(undefined);
    }
  }, [response, onSuccess, enqueueSnackbar, dispatch]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
    }
  }, [error, enqueueSnackbar]);

  const avatarSrc = userData?.image || personPlaceholderImage;
  const previewSrc = objectURL || avatarSrc;
  const showInitials = !userData?.image && !objectURL;

  return (
    <Fragment>
      <input
        type="file"
        ref={filRef}
        onChange={({ target: { files } }) => {
          if (!files) return;
          setFile(files[0]);
          setOpen(true);
        }}
        accept="image/jpeg, image/jpg, image/png, .png, .jpeg, .jpg"
        hidden
        readOnly
      />
      <MyModal
        open={open}
        setOpen={setOpen}
        title={t["Update profile picture"]}
        subtitle={t["Update profile picture description"]}
        variant="light"
      >
        <Avatar
          alt="profile"
          variant="circular"
          src={showInitials ? undefined : previewSrc}
          sx={{
            width: 160,
            height: 160,
            mx: "auto",
            fontSize: 48,
            fontWeight: 600,
            background: showInitials
              ? `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`
              : undefined,
            border: `1px solid ${COLORS.surface.border}`
          }}
        >
          {showInitials ? getInitials(userData?.name) : null}
        </Avatar>
        <Stack direction="row" mt={2.5} spacing={1} width="100%" justifyContent="center">
          {file ? (
            <Button
              variant="contained"
              color="primary"
              onClick={() => onSubmit()}
              disabled={isLoading}
              endIcon={isLoading ? <CircularProgress size={18} /> : undefined}
              sx={{ minWidth: 140, textTransform: "none", borderRadius: `${COLORS.radius.md}px` }}
            >
              {t["Update"]}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="primary"
              onClick={() => filRef?.current?.click()}
              sx={{ minWidth: 140, textTransform: "none", borderRadius: `${COLORS.radius.md}px` }}
            >
              {t["Select picture"]}
            </Button>
          )}
          {userData?.image && (
            <Button
              variant="outlined"
              color="error"
              disabled={isLoading}
              onClick={() => onSubmit(true)}
              sx={{ textTransform: "none", borderRadius: `${COLORS.radius.md}px` }}
            >
              {t["Remove picture"]}
            </Button>
          )}
        </Stack>
      </MyModal>

      <Badge
        overlap="circular"
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        badgeContent={
          <Tooltip title={t["Update profile picture"]}>
            <IconButton
              size="small"
              onClick={() => filRef?.current?.click()}
              sx={{
                width: 32,
                height: 32,
                bgcolor: COLORS.surface.card,
                border: `1px solid ${COLORS.surface.border}`,
                color: COLORS.surface.secondaryText,
                "&:hover": { bgcolor: COLORS.surface.tableHeader }
              }}
            >
              <PhotoCamera sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        }
      >
        <Avatar
          alt="profile"
          variant="circular"
          src={showInitials ? undefined : avatarSrc}
          sx={{
            width: size,
            height: size,
            fontSize: size * 0.34,
            fontWeight: 600,
            background: showInitials
              ? `linear-gradient(135deg, ${COLORS.gradients.button.left}, ${COLORS.gradients.button.right})`
              : undefined
          }}
        >
          {showInitials ? getInitials(userData?.name) : null}
        </Avatar>
      </Badge>
    </Fragment>
  );
}
